'use client';

import { Badge } from '@ahafood/ui/components/badge';
import { Button } from '@ahafood/ui/components/button';
import { Card } from '@ahafood/ui/components/card';
import { Image } from '@ahafood/ui/components/image';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { formatCurrency } from '@ahm/common-helpers';
import { ArrowRight, Check, Plus, Star } from 'lucide-react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useCallback, useEffect, useMemo, useState } from 'react';
import TrendingIcon from '@/../public/static/icons/trending-icon.svg';
import {
  useGetSoloMealItem,
  type SoloMealItem,
} from '@/api/use-get-solomeal-item';
import { useGoToOrderWebstore } from '@/api/use-webstore-order';

const LoginDialog = dynamic(
  () =>
    import('@/app/[locale]/(main)/store/components/fb-login').then((m) => ({
      default: m.LoginDialog,
    })),
  { ssr: false }
);
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { useRouter } from '@/i18n/routing';
import {
  calculateETA,
  formatDistanceMeterKilometer,
} from '@/lib/eta-calculator';
import { trackEvent } from '@/lib/event-tracking';
import { useAuth } from '@/providers/auth-provider';
import { useAiFoodOrderStore, useAuthStore } from '@/store';
import type { MenuItem } from '@/types/item';

const InactiveBadge = () => (
  <div className="absolute inset-0 bg-neutral-50/50 z-10 size-full rounded-xl">
    <Badge className="absolute bg-neutral-90 hover:bg-white top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 px-3 py-1.5 shadow-md md:px-4 md:py-2 md:text-sm">
      <span className="font-bold text-nowrap hover:text-neutral-90">
        Đóng cửa
      </span>
    </Badge>
  </div>
);

type SoloMealSectionProps = {
  lat: number;
  lng: number;
};

export default function SoloMealSection({ lat, lng }: SoloMealSectionProps) {
  const cityId = useAiFoodOrderStore.use.city_id();
  const routes = useCityRoutes();
  const router = useRouter();
  const { user } = useAuth();
  const token = user?.systemToken;

  const { isAuthenticated } = useAuthStore();
  const [showLoginDialog, setShowLoginDialog] = useState(false);

  // Chỉ gọi API khi có toạ độ thật. lat/lng === 0 = fallback khi address
  // chưa resolve → tránh request thừa và lỗi 4xx từ BE.
  const hasCoords = !!lat && !!lng;

  const { data, isLoading, error } = useGetSoloMealItem(
    {
      lat,
      lng,
      limit: 10,
      skip: 0,
    },
    token,
    { enabled: hasCoords }
  );

  useEffect(() => {
    if (isAuthenticated) {
      setShowLoginDialog(false);
    }
  }, [isAuthenticated]);

  const freeShipValue = useMemo(
    () =>
      cityId === 'DAD' || cityId === 'VCL' || cityId === 'DNI' ? '29k' : '33k',
    [cityId]
  );

  if (isLoading) {
    return (
      <section className="pb-6 px-4">
        <div className="flex items-center justify-between mb-4">
          <div className="h-8 bg-neutral-20 rounded animate-pulse w-32"></div>
          <div className="h-6 bg-neutral-20 rounded animate-pulse w-20"></div>
        </div>
        <div className="flex gap-4 overflow-x-auto scrollbar-hide">
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              className="min-w-[280px] h-64 bg-neutral-20 rounded-lg animate-pulse"
            ></div>
          ))}
        </div>
      </section>
    );
  }

  if (error ?? !data?.solo_meal_items?.length) {
    return null;
  }

  return (
    <section className="bg-transparent mt-4">
      {/* Section banner — full-width edge-to-edge (match Flash Deal pattern) */}
      <Card
        className="pb-10 pt-2 px-4 border-none shadow-none bg-gradient-to-b from-secondary-30/80 via-secondary-10 via-20% to-white to-90% w-full max-w-screen-xl mx-auto rounded-t-none md:rounded-t-3xl"
        onClick={() => {
          trackEvent('click_button', {
            action_time: Math.floor(Date.now() / 1000),
            button_name: 'click_solomeal',
            button_location: 'solomeal_banner',
            user_id: user?.id ?? '',
          });
          router.push(routes.solomeal);
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex flex-col gap-0">
            <img
              className="w-36 h-auto"
              src={`${assetPrefix}/static/images/solo-meal-section.png`}
              alt="solo meal"
              data-testid="home-solomeal-title"
            />
            <div
              className="flex items-center gap-1 text-sm "
              data-testid="home-solomeal-subtitle"
            >
              <span className="size-4 text-blue-10 bg-secondary-50 rounded-full flex items-center justify-center [&_svg]:size-3">
                <Check />
              </span>
              <span className="text-secondary-50 font-bold">
                Bữa ăn {freeShipValue} + Miễn phí vận chuyển
              </span>
            </div>
          </div>
          <Link
            href={routes.solomeal}
            scroll
            prefetch
            className="shrink-0 size-10 rounded-full bg-secondary-50 text-white shadow-md flex items-center justify-center hover:bg-secondary-60 transition-colors"
          >
            <ArrowRight className="size-5" />
          </Link>
        </div>

        {/* Horizontal Scrolling Cards */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2 -mx-4 px-4 md:mx-0 md:px-0">
          {data.solo_meal_items.map((item, index) => (
            <SoloMealCard
              key={item._id}
              item={item}
              index={index}
              isAuthenticated={isAuthenticated}
              setShowLoginDialog={setShowLoginDialog}
            />
          ))}
        </div>
      </Card>
      <LoginDialog
        open={showLoginDialog}
        onOpenChange={setShowLoginDialog}
        location="solo_meal_section"
      />
    </section>
  );
}

interface SoloMealCardProps {
  item: SoloMealItem;
  index: number;
  isAuthenticated: boolean;
  setShowLoginDialog: (show: boolean) => void;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function SoloMealCard({
  item,
  index,
  isAuthenticated,
  setShowLoginDialog,
}: SoloMealCardProps) {
  const goToOrderWebstore = useGoToOrderWebstore();
  const router = useRouter();
  const cityId = useAiFoodOrderStore.use.city_id();
  const isClosed = item.is_temporary_closed;
  const { user } = useAuth();
  const routes = useCityRoutes();

  // Follow RatingBadge logic
  let rawRating: number | null = null;
  const showSold = item.store_total_sold > 10;
  let soldDisplay = '';
  if (showSold) {
    soldDisplay =
      item.store_total_sold > 999
        ? `${(item.store_total_sold / 1000).toFixed(1)}K`
        : `${item.store_total_sold}`;
  }
  if (item.rating?.average && item.rating.average > 0) {
    rawRating = item.rating.average;
  } else if (item.rating?.google_average && item.rating.google_average >= 4) {
    rawRating = item.rating.google_average;
  }
  const showRating = rawRating !== null;

  const handleOrderNow = useCallback(
    async (item: SoloMealItem, e: React.MouseEvent) => {
      e.stopPropagation();
      if (!isAuthenticated) {
        setShowLoginDialog(true);
        return;
      }

      const convertedItem: MenuItem = {
        _id: item._id,
        name: item.name,
        price: item.price,
        image_thumb_url: item.image_thumb_url,
        discounted_price: item.discounted_price,
        is_ai_image: item.is_ai_image ?? false,
      };

      try {
        await goToOrderWebstore(
          item.store_id.toString(),
          item.store_slug,
          '',
          convertedItem,
          item.store_id?.toString() || '',
          'solomeal'
        );
        router.push(routes.storeDetail(item.store_slug));
      } catch (error) {
        console.error('Failed to go to order webstore:', error);
      }
    },
    [isAuthenticated, setShowLoginDialog, goToOrderWebstore, router, routes]
  );

  const handleCardClick = useCallback(() => {
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'click_solomeal_card',
      button_location: 'solomeal_card',
      store_id: item.store_id?.toString() || '',
      user_id: user?.id ?? '',
    });
    router.push(routes.storeDetail(item.store_slug));
  }, [router, routes, item.store_slug, item.store_id, user?.id]);

  if (isClosed) {
    return (
      <Card className="w-[150px] shadow-none overflow-hidden border-none relative shrink-0 p-0 bg-transparent">
        <InactiveBadge />
        <div className="relative w-full aspect-square">
          <Image
            src={
              item.image_thumb_url ||
              `${assetPrefix}/static/images/empty-store-bot.webp`
            }
            alt={item.name}
            fill
            className="object-cover rounded-md"
            sizes="full"
          />
        </div>
      </Card>
    );
  }

  return (
    <Card
      onClick={(e) => {
        e.stopPropagation();
        handleCardClick();
      }}
      className="w-[150px] shadow-none overflow-hidden border-none relative shrink-0 p-0 bg-transparent block cursor-pointer hover:opacity-90"
    >
      <div className="relative w-full aspect-square">
        <Image
          src={
            item.image_thumb_url ||
            `${assetPrefix}/static/images/empty-store-bot.webp`
          }
          alt={item.name}
          fill
          className="object-cover rounded-md"
          sizes="full"
          data-testid="home-solo-item-image"
        />

        {/* AI Badge — nhỏ hơn */}
        {item.is_ai_image && (
          <Badge className="size-5 absolute top-1.5 right-1.5 bg-white/60 hover:bg-white/90 text-white border-none shadow-sm p-0.5">
            <Image
              fill
              className="p-0.5"
              src={`${assetPrefix}/static/icons/white-ai.svg`}
              alt={''}
            />
          </Badge>
        )}

        {/* ETA Badge với badge-tail (giữ AhaFood specific) */}
        <div className="absolute bottom-0 left-0 flex items-end gap-0 text-neutral-70">
          <Badge
            variant="secondary"
            className="flex items-center w-fit h-5 bg-white rounded-none rounded-tr-2xl"
            data-testid="home-solo-item-eta"
          >
            <img
              src={`${assetPrefix}/static/icons/clock.svg`}
              alt="ETA"
              className="mr-1"
            />
            <span className="text-[10px] text-center text-neutral-60 w-full font-semibold pt-1 pr-1">
              Từ {calculateETA(cityId, item.distance) ?? 20} phút
            </span>
          </Badge>
          <img
            src={`${assetPrefix}/static/icons/badge-tail.svg`}
            alt="ETA"
            className="size-4 object-contain pointer-events-none -mb-0.5 -ml-0.5"
          />
        </div>

        {/* Add Button */}
        <Button
          onClick={(e) => handleOrderNow(item, e)}
          size="icon"
          className="absolute bottom-1.5 right-1.5 size-6 rounded-full bg-white hover:bg-neutral-5 text-primary-50 shadow-md"
          variant="ghost"
          data-testid="home-solo-item-add"
        >
          <Plus className="size-4" />
        </Button>
      </div>

      {/* Info */}
      <div className="pt-1.5 flex flex-col gap-1">
        <h3 className="font-medium text-xs text-neutral-90 leading-tight line-clamp-2 min-h-8">
          {item.trend_item ? (
            <TrendingIcon className="inline-block mr-1 animate-fire-pulse" />
          ) : null}
          {item.name}
        </h3>

        <div className="flex items-baseline gap-1.5 flex-wrap">
          <span className="font-bold bg-[#FEBC2F] px-1 text-sm">
            {formatCurrency(item.discounted_price || item.price)}
          </span>
          {item.discounted_price && item.discounted_price < item.price && (
            <span className="text-[10px] text-neutral-40 line-through">
              {formatCurrency(item.price)}
            </span>
          )}
        </div>

        <div className="text-[11px] text-neutral-60 flex justify-between gap-1.5">
          <span className="truncate flex-1">{item.store_name}</span>
          <span className="shrink-0">
            {formatDistanceMeterKilometer(item.distance)}
          </span>
        </div>

        {/* Rating + sold — inline plain text, không Badge wrapper */}
        {showRating || showSold ? (
          <div className="flex items-center gap-1 text-[11px] text-neutral-60">
            {showRating && (
              <div className="flex items-center gap-0.5">
                {item.rating?.total !== 0 ? (
                  <Star className="size-3 fill-[#FEBC2F] text-[#FEBC2F]" />
                ) : (
                  <img
                    src="/static/images/gg-rating.webp"
                    alt="Store"
                    className="inline-block mr-1 h-auto w-3 mb-0.5"
                  />
                )}
                <span>{rawRating!.toFixed(1)}</span>
              </div>
            )}
            {showRating && showSold ? (
              <span className="opacity-50">|</span>
            ) : null}
            {showSold ? <span>{soldDisplay} lượt bán</span> : null}
          </div>
        ) : null}
      </div>
    </Card>
  );
}
